import { Warehouse } from '@mui/icons-material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Button, Chip, CircularProgress, Stack } from '@mui/material';
import { useRef, useState } from 'react';
import { upsertStockDelta } from '../../../../shared/api/scenarioApi';
import { StorageKey } from '../../../../shared/auth/DataService';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';
import ScenarioModeAlert from '../../../../shared/components/display/ScenarioModeAlert';
import MBModal from '../../../../shared/components/modals/MBModal';
import { useScenario } from '../../../../shared/contexts/ScenarioContext';
import useScenarioMode from '../../../../shared/hooks/useScenarioMode';
import { useReportingCoordination } from '../../../vessel/store';
import { addStock } from '../../services/StockRepository';
import { buildStockPatch } from '../../utils/buildStockPatch';
import RHFStockForm, { type RHFStockFormValues } from './RHFStockForm';

// ── Types ─────────────────────────────────────────────────────

export interface StockModalProps {
  isModalOpen: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
}

// ── Component ─────────────────────────────────────────────────

const StockModal = ({
  isModalOpen,
  onClose = () => undefined,
  onSuccess,
}: StockModalProps) => {
  const submitRef = useRef<HTMLFormElement | null>(null);
  const { fetchAllData } = useReportingCoordination();
  const { isScenarioActive, scenarioLabel } = useScenarioMode();
  const { activeScenario } = useScenario();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info' = 'success'
  ) => setSnackbar({ open: true, message, severity });

  const handleSubmit = async (formData: RHFStockFormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const emea = localStorage.getItem(StorageKey.userInfo_sub);

    if (isScenarioActive && activeScenario) {
      try {
        await upsertStockDelta(activeScenario.id, {
          changeType: 'MODIFIED',
          patch: buildStockPatch(formData),
        });
        showSnackbar('Stock scenario change saved', 'success');
        fetchAllData();
        onSuccess?.();
        onClose();
      } catch (error: any) {
        console.error('Error saving stock scenario change:', error);
        showSnackbar(
          error?.message || 'Failed to save stock scenario change.',
          'error'
        );
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const payload = { ...formData } as any;
    if (emea) {
      payload.holds = payload.holds.map((hold: any) => ({
        ...hold,
        updatedBy: emea,
      }));
      payload.updatedBy = emea;
    }
    try {
      await addStock(payload);
      showSnackbar('Stock added successfully', 'success');
      fetchAllData();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error adding stock:', error);
      showSnackbar(
        error?.message || 'Failed to add stock. Please try again.',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isSubmitting;

  return (
    <>
      <MBModal
        titleIcon={<Warehouse fontSize='medium' />}
        fullWidth
        maxWidth='md'
        open={isModalOpen}
        onClose={onClose}
        disableClose={isSubmitting}
        title={
          isScenarioActive ? (
            <Stack direction='row' alignItems='center' spacing={1}>
              <span>Stock Management</span>
              <Chip
                icon={<ScienceOutlinedIcon />}
                label={scenarioLabel('Scenario Mode')}
                color='warning'
                size='small'
              />
            </Stack>
          ) : (
            'Stock Management'
          )
        }
        actions={
          <Stack direction='row' spacing={2} sx={{ padding: '20px 24px' }}>
            <Button
              variant='outlined'
              color='secondary'
              onClick={onClose}
              disableElevation
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={() =>
                submitRef.current && submitRef.current.requestSubmit()
              }
              disableElevation
              type='submit'
              disabled={isBusy}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={18} color='inherit' />
                ) : undefined
              }
            >
              {scenarioLabel('Add Stock')}
            </Button>
          </Stack>
        }
      >
        <ScenarioModeAlert />
        <RHFStockForm onSubmit={handleSubmit} ref={submitRef} />
      </MBModal>
      <ExpSnackbarAlerts
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default StockModal;
